<script setup>
/**
 * ImmersivePromoItemRow
 * One item inside the promo items modal: image, name, live effective price
 * (promo-aware via the shared useItemPricing), and a compact qty stepper that
 * writes straight to the cart — the modal's cumulative step math reacts live.
 */
import { computed, ref, watch } from 'vue'
import { useCartStore } from '../../../../app/store/cart.js'
import { useItemPricing } from '../../../../core/composables/useItemPricing.js'
import { hapticTap } from '../../../../core/utils/haptics.js'
import ItemDialogQtyStepper from '../item-dialog/actions/ItemDialogQtyStepper.vue'

const props = defineProps({
  item: { type: Object, required: true },
  showPrices: { type: Boolean, default: true },
})

const cartStore = useCartStore()

const itemRef = computed(() => props.item)
const {
  isOutOfStock,
  hasValidPrice,
  cartQty,
  isInCart,
  isOnlyBox,
  boxSize,
  stepSize,
  hasPromo,
  formattedPrice,
  originalPrice,
} = useItemPricing(itemRef)

const makat = computed(() => props.item?.makat || null)
const disabled = computed(() => !hasValidPrice.value || isOutOfStock.value)

const imageUrl = computed(() => {
  const img = props.item?.image
  return typeof img === 'string' && img.trim().length > 0 ? img : null
})

const showBoxMessage = ref(false)

function handleQtyUpdate(newQty) {
  if (!makat.value || disabled.value) return
  const result = cartStore.setQty(makat.value, newQty)
  if (!result.ok) {
    if (
      result.message?.startsWith('QTY_NOT_BOX_MULTIPLE') ||
      result.message === 'INVALID_BOXSIZE'
    ) {
      showBoxMessage.value = true
    }
  }
}

function handleRemove() {
  if (!makat.value) return
  cartStore.removeItem(makat.value)
}

function handleInvalid() {
  if (isOnlyBox.value) showBoxMessage.value = true
}

watch(cartQty, (nv, ov) => {
  if (nv !== ov) hapticTap()
})
</script>

<template>
  <div class="imm-pr-row" :class="{ 'is-in-cart': isInCart }">
    <div class="imm-pr-thumb">
      <img v-if="imageUrl" :src="imageUrl" :alt="item.name" loading="lazy" />
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    </div>

    <div class="imm-pr-info">
      <span class="imm-pr-name">{{ item.name || 'פריט ללא שם' }}</span>
      <span class="imm-pr-makat">מק"ט {{ item.makat }}</span>
      <div v-if="showPrices" class="imm-pr-price">
        <template v-if="isOutOfStock">
          <span class="imm-pr-oos">חסר זמנית במלאי</span>
        </template>
        <template v-else-if="hasValidPrice">
          <span
            v-if="hasPromo && originalPrice && originalPrice !== formattedPrice"
            class="imm-pr-orig"
            >₪{{ originalPrice }}</span
          >
          <span class="imm-pr-value">₪{{ formattedPrice }}</span>
        </template>
        <span v-else class="imm-pr-oos">אין מחיר זמין</span>
      </div>
      <div v-if="showBoxMessage && isOnlyBox" class="imm-pr-boxmsg">
        מכפלות של {{ boxSize }} בלבד
      </div>
    </div>

    <div class="imm-pr-stepper">
      <ItemDialogQtyStepper
        :qty="cartQty"
        :step="stepSize"
        :only-box="isOnlyBox"
        :box-size="boxSize"
        :disabled="disabled"
        @update:qty="handleQtyUpdate"
        @remove="handleRemove"
        @invalid="handleInvalid"
      />
    </div>
  </div>
</template>

<style scoped>
.imm-pr-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid transparent;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.imm-pr-row.is-in-cart {
  background: var(--imm-field-bg, rgba(255, 255, 255, 0.06));
  border-color: var(--imm-field-border, rgba(255, 255, 255, 0.14));
}

.imm-pr-thumb {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.imm-pr-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 3px;
}

.imm-pr-thumb svg {
  width: 26px;
  height: 26px;
  color: rgba(15, 23, 42, 0.3);
}

.imm-pr-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: start;
}

.imm-pr-name {
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--imm-text, #ffffff);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.imm-pr-makat {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--imm-text-faint, rgba(255, 255, 255, 0.5));
  font-variant-numeric: tabular-nums;
}

.imm-pr-price {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.imm-pr-orig {
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--imm-text-faint, rgba(255, 255, 255, 0.5));
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
}

.imm-pr-value {
  font-size: 1rem;
  font-weight: 800;
  color: var(--imm-text, #ffffff);
  font-variant-numeric: tabular-nums;
}

.imm-pr-oos {
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--imm-error, #f87171);
}

.imm-pr-boxmsg {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--imm-warning, #fbbf24);
}

/* Compact stepper — sized for list rows, not the pane hero control. Remaps the
   design tokens the shared stepper consumes onto the immersive theme (same
   pattern as ImmersiveQuickAdd). */
.imm-pr-stepper {
  flex-shrink: 0;
  --bg-secondary: var(--imm-field-bg, rgba(255, 255, 255, 0.07));
  --bg-tertiary: var(--imm-step-active-bg, rgba(59, 130, 246, 0.22));
  --border-color: var(--imm-field-border, rgba(255, 255, 255, 0.18));
  --text-primary: var(--imm-text, #ffffff);
  --color-primary: var(--imm-accent, #3b82f6);
}

.imm-pr-stepper :deep(.qty-stepper) {
  border-radius: 14px;
}

.imm-pr-stepper :deep(.stepper-btn) {
  width: 46px;
  height: 46px;
}

.imm-pr-stepper :deep(.qty-input) {
  width: 48px;
  height: 46px;
  font-size: 1.1rem;
  font-variant-numeric: tabular-nums;
}

/* ── Tablet/PC: the row becomes a card tile inside the modal's grid —
   image on top, centered info, full-width stepper pinned to the bottom. ── */
@media (min-width: 768px) {
  .imm-pr-row {
    flex-direction: column;
    align-items: stretch;
    height: 100%;
    gap: 10px;
    padding: 14px;
    border-radius: 18px;
    background: var(--imm-field-bg, rgba(255, 255, 255, 0.05));
    border-color: var(--imm-field-border, rgba(255, 255, 255, 0.1));
  }

  .imm-pr-row.is-in-cart {
    border-color: var(--imm-accent, #3b82f6);
  }

  .imm-pr-thumb {
    width: 100%;
    height: 140px;
    border-radius: 14px;
  }

  .imm-pr-thumb img {
    padding: 8px;
  }

  .imm-pr-info {
    align-items: center;
    text-align: center;
    gap: 3px;
  }

  .imm-pr-name {
    /* Reserve two lines so price + stepper align across the grid row */
    min-height: 2.5em;
  }

  .imm-pr-price {
    justify-content: center;
  }

  .imm-pr-stepper {
    margin-top: auto;
    width: 100%;
  }

  .imm-pr-stepper :deep(.qty-stepper) {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
